import React, { useContext, useState } from 'react';
import { Box, Button, CircularProgress, Typography, useTheme } from '@mui/material';
import { useAuth } from 'oidc-react';
import { Analytics, FlashOn, Insights } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { UIContext } from '../context/UIContext';
import { Banner } from '../api/promotionalBanner';
import EnhancedSnackbar from './EnhancedSnackbar';
import { DASHBOARD_DEFAULT_PERMISSIONS } from '../constants';
import { calculateAcces } from './FeatureGuardContainer';

interface PromotionalBannerProps {
    banner: Banner;
    isLoading?: boolean;
    isSubmitting?: boolean;
    isError?: boolean;
    submitError?: boolean;
    onInterested: () => Promise<void> | void;
    onDismiss: () => void;
}

const PromotionalBanner: React.FC<PromotionalBannerProps> = ({ banner, isLoading, isSubmitting, isError, submitError, onInterested, onDismiss }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { isMobile } = useContext(UIContext);

    // Color variables for easier customization
    const BANNER_BG_COLOR = theme.palette.mode === 'light' ? theme.palette.darkBlue.main : theme.palette.primary.dark;
    const BUTTON_COLOR = theme.palette.mode === 'light' ? theme.palette.primary.main : theme.palette.darkBlue.main;
    const ICON_COLOR = theme.palette.darkBlue.main;

    if (isLoading) {
        return (
            <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    const header = t(`components.promotionalBanner.${banner.feature}.header`);
    const description = t(`components.promotionalBanner.${banner.feature}.description`);
    const interestedButtonText = t(`components.promotionalBanner.${banner.feature}.interestedButtonText`, { defaultValue: null });
    const dismissButtonText = t(`components.promotionalBanner.${banner.feature}.dismissButtonText`, {
        defaultValue: null,
    });

    const highlights = [
        {
            icon: <FlashOn />,
            text: t(`components.promotionalBanner.${banner.feature}.highlights.highlight_1`, {
                defaultValue: null,
            }),
        },
        {
            icon: <Insights />,
            text: t(`components.promotionalBanner.${banner.feature}.highlights.highlight_2`, {
                defaultValue: null,
            }),
        },
        {
            icon: <Analytics />,
            text: t(`components.promotionalBanner.${banner.feature}.highlights.highlight_3`, {
                defaultValue: null,
            }),
        },
    ].filter((item): item is { icon: JSX.Element; text: string } => Boolean(item.text));

    return (
        <>
            <Box
                sx={{
                    backgroundColor: BANNER_BG_COLOR,
                    color: theme.palette.primary.contrastText,
                    padding: theme.spacing(3),
                    borderRadius: theme.spacing(1),
                    marginBottom: theme.spacing(2),
                    marginX: isMobile ? theme.spacing(2) : 0,
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
                    <Typography variant="h2" sx={{ fontSize: '24px', lineHeight: '30px' }}>
                        {header}
                    </Typography>

                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line', letterSpacing: '0.15px' }}>
                        {description}
                    </Typography>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: isMobile ? 'stretch' : 'center',
                        flexWrap: 'wrap',
                        marginTop: theme.spacing(3),
                        flexDirection: isMobile ? 'column' : 'row',
                        gap: theme.spacing(2),
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: isMobile ? 'flex-start' : 'center',
                            flexDirection: isMobile ? 'column' : 'row',
                            gap: theme.spacing(isMobile ? 2 : 3),
                            flex: isMobile ? '1' : 'auto',
                            flexWrap: 'wrap',
                        }}
                    >
                        {highlights &&
                            highlights.map((highlight) => (
                                <Box
                                    key={highlight.text}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: theme.spacing(1),
                                        flex: isMobile ? '1 1 100%' : 'none',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            backgroundColor: theme.palette.common.white,
                                            borderRadius: '50%',
                                            padding: theme.spacing(0.5),
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            height: theme.spacing(2.5),
                                            width: theme.spacing(2.5),
                                            minWidth: theme.spacing(2.5),
                                            '& svg': { fill: ICON_COLOR, fontSize: theme.spacing(2) },
                                        }}
                                        aria-hidden="true"
                                    >
                                        {highlight.icon}
                                    </Box>

                                    <Typography variant="body2" sx={{ marginTop: '2px' }}>
                                        {highlight.text}
                                    </Typography>
                                </Box>
                            ))}
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: isMobile ? 'column-reverse' : 'row',
                            gap: theme.spacing(1),
                            width: isMobile ? '100%' : 'auto',
                            flexShrink: 0,
                        }}
                    >
                        <Button variant="text" onClick={onDismiss} sx={{ color: theme.palette.blue.light }}>
                            <Typography variant="button" sx={{ textTransform: 'none', width: isMobile ? '100%' : 'auto' }}>
                                {dismissButtonText ?? t('components.promotionalBanner.default.dismiss')}
                            </Typography>
                        </Button>

                        <Button
                            variant="contained"
                            onClick={onInterested}
                            disabled={isSubmitting}
                            sx={{
                                backgroundColor: BUTTON_COLOR,
                                color: theme.palette.common.white,
                                '&:hover': {
                                    color: theme.palette.common.white,
                                    backgroundColor: BUTTON_COLOR,
                                },
                            }}
                        >
                            <Typography variant="button" sx={{ textTransform: 'none', width: isMobile ? '100%' : 'auto' }}>
                                {interestedButtonText ?? t('components.promotionalBanner.default.interested')}
                            </Typography>
                        </Button>
                    </Box>
                </Box>
            </Box>
            <EnhancedSnackbar open={!!(isError || submitError)} message={t('components.error.somethingFailed')} severity="error" />
        </>
    );
};

export default PromotionalBanner;
